import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteCard from '@/components/base/SiteCard';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import { getCategoryBySlug, getSitesByCategory } from '@/utils/nav';

const PAGE_SIZE = 9;
const regions = ['全部', '全国', '海外'];
const sorts = [
  { key: 'hot', label: '热度排序' },
  { key: 'name', label: '名称排序' },
  { key: 'new', label: '最新收录' },
];

export default function Category() {
  const { categoryName = '' } = useParams();
  const category = getCategoryBySlug(categoryName);

  const [activeSub, setActiveSub] = useState('全部');
  const [activeRegion, setActiveRegion] = useState('全部');
  const [activeSort, setActiveSort] = useState('hot');
  const [page, setPage] = useState(1);

  useSeo({
    title: category ? `${category.label} - 蓝导航` : '分类未找到 - 蓝导航',
    description: category
      ? category.description
      : '未找到对应的导航分类，请返回首页重新选择。',
    keywords: category
      ? `蓝导航,${category.label},${category.label}导航,网站导航,快捷入口`
      : '蓝导航,网站导航',
  });

  const list = useMemo(() => {
    if (!category) return [];
    let result = getSitesByCategory(category.slug);
    if (activeSub !== '全部') {
      result = result.filter((site) => site.subCategory === activeSub);
    }
    if (activeRegion !== '全部') {
      result = result.filter((site) => site.region === activeRegion);
    }
    return [...result].sort((a, b) => {
      if (activeSort === 'name') return a.name.localeCompare(b.name, 'zh-Hans-CN');
      if (activeSort === 'new') return a.addedAt < b.addedAt ? 1 : -1;
      return b.hot - a.hot;
    });
  }, [category, activeSub, activeRegion, activeSort]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageSites = list.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  if (!category) {
    return (
      <EmptyState
        icon="ri-error-warning-line"
        title="未找到该分类"
        description="你访问的分类不存在或暂时没有收录内容，请返回首页重新选择。"
        action={{ to: '/', label: '返回首页' }}
      />
    );
  }

  const updateSub = (value: string) => {
    setActiveSub(value);
    setPage(1);
  };
  const updateRegion = (value: string) => {
    setActiveRegion(value);
    setPage(1);
  };
  const updateSort = (value: string) => {
    setActiveSort(value);
    setPage(1);
  };

  return (
    <div className="w-full">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="cursor-pointer hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-700">分类导航</span>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{category.label}</span>
      </nav>

      <header className="mt-4 flex flex-wrap items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary-500 text-2xl text-background-50">
          <i className={category.icon}></i>
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">
            {category.label}
          </h1>
          <p className="mt-1 text-sm text-foreground-600">{category.description}</p>
        </div>
        <span className="rounded-full bg-background-50 px-3 py-1.5 text-xs text-foreground-600">
          共 <strong className="font-semibold text-primary-600">{list.length}</strong> 个站点
        </span>
      </header>

      <div className="mt-6">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-foreground-400">
          子分类
        </p>
        <div className="flex flex-wrap gap-2">
          {['全部', ...category.subCategories].map((sub) => (
            <button
              key={sub}
              type="button"
              onClick={() => updateSub(sub)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                activeSub === sub
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
              }`}
            >
              {sub}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-500">地区</span>
          <div className="flex rounded-full bg-background-100 p-1">
            {regions.map((region) => (
              <button
                key={region}
                type="button"
                onClick={() => updateRegion(region)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                  activeRegion === region
                    ? 'bg-background-50 font-medium text-primary-600'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {region}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-500">排序</span>
          <div className="flex rounded-full bg-background-100 p-1">
            {sorts.map((sort) => (
              <button
                key={sort.key}
                type="button"
                onClick={() => updateSort(sort.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                  activeSort === sort.key
                    ? 'bg-background-50 font-medium text-primary-600'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {sort.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon="ri-filter-off-line"
            title="没有符合条件的结果"
            description="当前筛选条件下暂未找到相关网站，试试重置筛选条件后再看看。"
          />
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => {
                setActiveSub('全部');
                setActiveRegion('全部');
                setActiveSort('hot');
                setPage(1);
              }}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pageSites.map((site) => (
              <SiteCard key={site.id} site={site} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-left-s-line"></i>
                上一页
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                (item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setPage(item)}
                    className={`h-9 w-9 cursor-pointer whitespace-nowrap rounded-md text-sm transition-colors ${
                      currentPage === item
                        ? 'bg-primary-500 font-medium text-background-50'
                        : 'border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    {item}
                  </button>
                ),
              )}

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                下一页
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}